import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search, RefreshCw, ShieldCheck, ShieldOff, AlertTriangle, Info } from "lucide-react";
import { apiUtils } from "../../../utils/apiUtils";
import SimpleModal from "../../composite/SimpleModal";

/**
 * StaffAccessManagement - grant or revoke the `global-admin` platform permission.
 *
 * Only ROOT account holders are listed. Team members inside a customer's
 * workspace are deliberately excluded: letting a customer's own admin promote a
 * colleague to RepCheck staff would be a way out of the workspace boundary. The
 * server enforces this too - this list just avoids offering the impossible.
 */

export default function StaffAccessManagement() {
  const [users, setUsers] = useState([]);
  const [staffCount, setStaffCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [confirm, setConfirm] = useState(null); // { user, action }
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiUtils.get("/api/staff-access/users");
      setUsers(res?.data?.data || []);
      setStaffCount(res?.data?.staffCount || 0);
    } catch (err) {
      setError(
        err.response?.status === 403
          ? "You need global admin access to manage staff."
          : "Could not load users."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => !q || [u.name, u.email].some((v) => (v || "").toLowerCase().includes(q)))
      // staff first, so the sensitive list is what you see
      .sort((a, b) => (b.isStaff ? 1 : 0) - (a.isStaff ? 1 : 0) || (a.name || "").localeCompare(b.name || ""));
  }, [users, search]);

  const act = async (user, action) => {
    setBusyId(user.id);
    setConfirm(null);
    try {
      const res = await apiUtils.post(`/api/staff-access/users/${user.id}/${action}`);
      const body = res?.data || {};
      setToast({ type: body.emailSent === false ? "warn" : "success", message: body.message });
      await load();
    } catch (err) {
      setToast({
        type: "error",
        message: err.response?.data?.message || `Could not ${action} staff access.`,
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-5">
      {toast && (
        <div
          className={`rounded-lg border p-3 text-sm ${
            toast.type === "error"
              ? "border-red-200 bg-red-50 text-red-800 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200"
              : toast.type === "warn"
              ? "border-amber-200 bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-200"
              : "border-emerald-200 bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-200"
          }`}
          role="status"
        >
          {toast.message}
        </div>
      )}

      <div className="flex items-start gap-2 rounded-lg border border-brand-200 bg-brand-50 p-3 text-sm text-brand-900 dark:bg-brand-500/10 dark:border-brand-500/30 dark:text-brand-200">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Global admins can read <strong>every customer's data</strong> - scans, monitored
          accounts and support tickets - and can approve pilot applications. Only root
          account holders appear here; team members inside a customer's workspace are not
          eligible.
        </span>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or email…"
            className="w-full rounded-lg border border-input py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>
        <span className="text-sm text-muted-foreground">
          {staffCount} admin{staffCount === 1 ? "" : "s"}
        </span>
        <button
          onClick={load}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-input px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
        </div>
      )}

      {loading && !users.length ? (
        <div className="rounded-xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">
          Loading users…
        </div>
      ) : !filtered.length ? (
        <div className="rounded-xl border border-dashed border-input bg-card p-10 text-center text-sm text-muted-foreground">
          No root account holders match that search.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
          <ul className="divide-y divide-border">
            {filtered.map((u) => (
              <li key={u.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-foreground">{u.name}</span>
                    {u.isStaff && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 ring-1 ring-inset ring-brand-600/20 dark:bg-brand-500/10 dark:text-brand-200">
                        <ShieldCheck className="h-3 w-3" /> Global admin
                      </span>
                    )}
                    {u.isSelf && <span className="text-xs text-muted-foreground">(you)</span>}
                    {u.status !== "active" && (
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{u.status}</span>
                    )}
                  </div>
                  <p className="truncate text-sm text-muted-foreground">{u.email}</p>
                </div>

                <div className="shrink-0">
                  {u.isStaff ? (
                    <button
                      disabled={busyId === u.id || u.isSelf}
                      title={u.isSelf ? "You can't revoke your own access" : undefined}
                      onClick={() => setConfirm({ user: u, action: "revoke" })}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-input px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <ShieldOff className="h-4 w-4" /> Revoke
                    </button>
                  ) : (
                    <button
                      disabled={busyId === u.id || u.status !== "active"}
                      title={u.status !== "active" ? `Account is ${u.status}` : undefined}
                      onClick={() => setConfirm({ user: u, action: "grant" })}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <ShieldCheck className="h-4 w-4" /> Make global admin
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <SimpleModal
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm?.action === "grant" ? "Grant global admin access?" : "Revoke global admin access?"}
      >
        {confirm && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {confirm.action === "grant" ? (
                <>
                  <strong>{confirm.user.name}</strong> ({confirm.user.email}) will be able to
                  read every customer's data, approve pilot applications and handle support
                  tickets. They'll be emailed to let them know.
                </>
              ) : (
                <>
                  <strong>{confirm.user.name}</strong> ({confirm.user.email}) will lose access
                  to the staff console immediately. Their own workspace is unaffected, and
                  they are not emailed - tell them yourself.
                </>
              )}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirm(null)}
                className="rounded-lg border border-input px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={() => act(confirm.user, confirm.action)}
                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${
                  confirm.action === "revoke" ? "bg-destructive hover:bg-destructive/90" : "bg-brand-600 hover:bg-brand-700"
                }`}
              >
                {confirm.action === "grant" ? "Grant access" : "Revoke access"}
              </button>
            </div>
          </div>
        )}
      </SimpleModal>
    </div>
  );
}
